// SignalsZone — the signals on one subject, on the Person and Organisation pages (§8, §13).
//
// A signal is an observation with the thing that proves it. So every row here carries its
// verbatim quote and the source that produced it; a row without a quote is only possible for
// `evidence_kind 'computed'`, which is arithmetic over rows we already hold and therefore makes
// no claim and carries no mark.
//
// The verbs are the same three the Signals page offers, because there is one vocabulary for
// disposing of a signal: a task the operator writes, a draft the operator approves, or a
// dismissal that is recorded as such. Nothing here writes to the record by itself.
import { useCallback, useEffect, useState } from 'react';
import { Att, Ev, Fig, Lnk, Record, Ref, Sep, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import { dispose as disposeSignal, listSignals } from '../api/signals.js';
import { ApiError } from '../api/client.js';
import { useToast } from './Toast.js';
import './SignalsZone.de.js';
import './SignalsZone.fr.js';
import type { Signal, SignalSeverity, SubjectKind } from '../../shared/types.js';

export interface SignalsZoneProps {
  /** camelCase for React, snake_case for the brief's own words — both are accepted. */
  subjectKind?: SubjectKind;
  subjectId?: number;
  subject_kind?: SubjectKind;
  subject_id?: number;
  limit?: number;
}

export function severityTone(severity: SignalSeverity): 'neutral' | 'attend' | 'broken' {
  return severity === 'high' ? 'broken' : severity === 'attend' ? 'attend' : 'neutral';
}

export default function SignalsZone(props: SignalsZoneProps) {
  const t = useT();
  const { fmtDate } = useLocale();
  const toast = useToast();

  const subjectKind = props.subjectKind ?? props.subject_kind;
  const subjectId = props.subjectId ?? props.subject_id;
  const limit = props.limit ?? 6;

  const [items, setItems] = useState<Signal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!subjectKind || !subjectId) return;
    try {
      const res = await listSignals({ subject_kind: subjectKind, subject_id: subjectId, limit });
      setItems(res.items);
      setError(null);
    } catch (err) {
      // A failed poll is silence, never a zero: the zone says it could not read, and the count
      // stays absent rather than becoming 0.
      setItems(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [subjectKind, subjectId, limit]);

  useEffect(() => { void load(); }, [load]);

  const act = useCallback(async (signal: Signal, disposition: 'task' | 'dismissed') => {
    setBusy(signal.id);
    try {
      const updated = await disposeSignal(signal.id, disposition);
      toast('ok', disposition === 'task'
        ? t('Task {ref} created', { ref: updated.disposition_ref ?? '' })
        : t('{ref} dismissed', { ref: updated.ref }));
      await load();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not dispose of the signal.'));
    } finally {
      setBusy(null);
    }
  }, [load, t, toast]);

  return (
    <Zone>
      <ZoneHead
        label={t('Signals')}
        count={items ? items.length : undefined}
        note={t('every signal cites what it was read from')}
        route={<button type="button" className="zone-route" onClick={() => open(ROUTES.signals())}>{t('Signals')} →</button>}
      />
      <Sheet clip={!!items?.length} pad={!items?.length}>
        {error ? (
          <p className="zone-empty">{t('The signals could not be read: {error}', { error })}</p>
        ) : items === null ? (
          <p className="zone-empty">{t('loading…')}</p>
        ) : items.length === 0 ? (
          <p className="zone-empty">
            {t('No monitor has raised anything for this subject. A signal is an observation with the snapshot, the publication or the snippet that proves it, and a suggested action. It never writes to the record: it proposes, and an operator holds or refuses it in Review.')}
          </p>
        ) : (
          items.map((signal) => (
            <div key={signal.id} className="row" style={{ gridTemplateColumns: '96px 88px 1fr 180px' }}>
              <Fig className="d">{fmtDate(signal.first_seen)}</Fig>
              <span><Tag tone={severityTone(signal.severity)}>{t(signal.kind)}</Tag></span>
              <span>
                <Record>{signal.headline}</Record>
                {signal.quote && (
                  <Ev src={<>
                    <Att kind="quoted" />
                    <Ref>{signal.evidence_ref ?? signal.ref}</Ref>
                    <Sep />
                    {t(signal.evidence_kind ?? 'manual')}
                    <Sep />
                    <Fig>{fmtDate(signal.first_seen)}</Fig>
                  </>}>{signal.quote}</Ev>
                )}
                {signal.detail && <div className="rat">{signal.detail}</div>}
              </span>
              <span className="flex gap-8">
                {signal.disposition === 'none' ? (
                  <>
                    <button type="button" className="btn btn-sm" disabled={busy === signal.id} onClick={() => void act(signal, 'task')}>
                      {t('Create task')}
                    </button>
                    <Lnk disabled={busy === signal.id} onClick={() => void act(signal, 'dismissed')}>{t('Dismiss')}</Lnk>
                  </>
                ) : (
                  <span className="d">
                    {t(signal.disposition)}
                    {signal.disposition_ref && <> <Ref>{signal.disposition_ref}</Ref></>}
                  </span>
                )}
              </span>
            </div>
          ))
        )}
      </Sheet>
    </Zone>
  );
}
